import {UploadFile as UploadFileIcon} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Link,
  Stack,
  Typography,
  lighten,
} from '@mui/material';
import {EVENT_ACTIONS} from '@src/analytics';
import {useTrackSectionEvent} from '@src/hooks/useTrackSectionEvent.js';
import formatNumber from '@src/utils/formatNumber.js';
import {array, bool, node, number, object} from 'prop-types';
import React from 'react';
import ReactDropzone from 'react-dropzone';
import {MAX_LOCAL_FILE_SIZE_BYTES} from './util.js';

// See https://react-dropzone.js.org/#section-accepting-specific-file-types
const DEFAULT_SUPPORTED_FILE_TYPES = {
  'application/json': ['.geojson', '.json'],
  'application/octet-stream': ['.shp', '.shx'],
  'application/x-dbf': ['.dbf'],
  'application/text': ['.prj', '.cpg'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
};

// ToDO: future work
// const ASYNC_SUPPORTED_FILE_TYPES = {
//   'application/octet-stream': ['.shp', '.shx', '.gpx'],
//   'application/geopackage+sqlite3': ['.gpkg'],
// };

const DEFAULT_SUPPORTED_EXTENSIONS = Object.values(
  DEFAULT_SUPPORTED_FILE_TYPES,
).flat();

export const DropZone = ({
  files = [],
  children,
  supportedExtensions = DEFAULT_SUPPORTED_EXTENSIONS,
  acceptedContentTypes = DEFAULT_SUPPORTED_FILE_TYPES,
  allowAdd = true,
  maxBytes = MAX_LOCAL_FILE_SIZE_BYTES,
  sx,
  ...props
}) => {
  const trackSectionEvent = useTrackSectionEvent();

  return (
    <ReactDropzone
      noClick
      accept={acceptedContentTypes}
      maxSize={maxBytes}
      {...props}
    >
      {({getRootProps, getInputProps, isDragActive, open}) => (
        <>
          <Stack
            {...getRootProps()}
            sx={[
              {
                margin: 2,
                borderWidth: files.length > 0 ? 0 : 2,
                borderStyle: 'dashed',
                borderRadius: 1,
                borderColor: isDragActive ? 'primary.main' : 'text.secondary',
                backgroundColor: theme =>
                  files.length > 0 && !isDragActive
                    ? 'paper'
                    : isDragActive
                      ? `rgba(${(theme.vars || theme).palette.primary.mainChannel} / (${(theme.vars || theme).palette.action.hoverOpacity} +
                            ${(theme.vars || theme).palette.action.activatedOpacity}))`
                      : `rgba(${(theme.vars || theme).palette.primary.mainChannel} / ${(theme.vars || theme).palette.action.activatedOpacity})`,
                outline: 'none',
                cursor: 'pointer',
                transition: 'color .24s ease-in-out, border .24s ease-in-out',
                alignItems: 'center',
                justifyContent: files.length > 0 ? 'flex-start' : 'center',
                flex: '1 1 auto',
                position: 'relative',
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
          >
            <input {...getInputProps()} />

            {files.length === 0 && (
              <>
                <Avatar
                  sx={theme => ({
                    backgroundColor: lighten(theme.palette.primary.light, 0.75),
                    color: 'primary.dark',
                    height: 56,
                    marginBlockStart: 2,
                    width: 56,
                  })}
                >
                  <UploadFileIcon fontSize="large" />
                </Avatar>

                <Typography
                  variant="h6"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    marginBlockEnd: 1,
                    textAlign: 'center',
                  }}
                >
                  <Link
                    color="primary"
                    component="button"
                    onClick={open}
                    variant="inherit"
                  >
                    Click to upload
                  </Link>{' '}
                  or drag and drop
                </Typography>

                <Typography
                  align="center"
                  sx={{marginBottom: 1, color: 'text.secondary'}}
                >
                  Supported file types: {supportedExtensions.join(', ')}
                </Typography>

                <Typography align="center" sx={{color: 'text.secondary'}}>
                  Maximum file size:{' '}
                  {formatNumber(maxBytes, {
                    notation: 'compact',
                    style: 'unit',
                    unit: 'byte',
                    unitDisplay: 'narrow',
                  })}
                </Typography>
              </>
            )}

            {files.length > 0 && (
              <>
                {children}

                {allowAdd && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginBlockStart: 'auto',
                      width: '100%',
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => {
                        trackSectionEvent(
                          EVENT_ACTIONS.OPEN,
                          'Uploader - Add More Files',
                        );
                        open();
                      }}
                    >
                      Add more files
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </>
      )}
    </ReactDropzone>
  );
};

DropZone.propTypes = {
  files: array,
  children: node,
  supportedExtensions: array,
  acceptedContentTypes: object,
  allowAdd: bool,
  maxBytes: number,
  sx: object,
};

export default DropZone;
